import { last } from 'underscore';
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { VerticalSeparatedLayout } from '@bingads-webui-react/steps';
import { StepFooter } from '@bingads-webui-campaign-react/step-wizard-layout';
import { getStepProgressItemGlyphicon } from '../styles/get-step-progress-item-glyphicon';
import { footerStyleOverrides } from '../styles/footer-style-overrides';

const {
  StepsContextProvider,
  StepContainer,
  StepProgressList,
} = VerticalSeparatedLayout;

// Navigation buttons scenarios:
//    - 1st step:
//      - Before campaign is created:
//        - Next button: "Save & go to the next step"
//        - Cancel button: "Cancel"
//      - After campaign is created:
//        - Next button: "Save & go to the next step"
//        - Exit button: "Save & exit"
//    - [2nd..last) steps:
//      - Next button:  "Save & go to the next step"
//      - Back button: "Back"
//      - Exit button: "Save & exit"
//    - last step (with data entry):
//      - Next/Exit button: "Save"
//      - Back button: "Back"
//    - last step (read-only):
//      - Next/Exit button: "Done"
//      - Back button: "Back"

export const MultiStepLayout = React.memo(({
  children,
  wizardState: {
    i18n, stepInfo, isBusy, stepIndex, stepListWithServiceInfo,
  },
  wizardActions: {
    saveAndNavigateNext, saveAndNavigateBack, cancelAndExit, saveAndExit,
  },
  classes,
}) => {
  const stepFooter = useMemo(() => {
    const beforeLastStepNextButtonText = i18n.getString(_TL_('Save & go to next step'));
    const saveAndExitButtonText = i18n.getString(_TL_('Save & exit'));
    const previousButtonText = i18n.getString(_TL_('Back'));
    if (stepIndex === 0) {
      // 1st step
      // will use the presence of 2nd step's stepService
      // as the fact that campaign has been saved
      if (stepListWithServiceInfo[1].stepService) {
        // After campaign is created
        return (
          <StepFooter
            className={classNames('wizard-step-footer', classes.wizardFooter)}
            styleOverrides={footerStyleOverrides}
            nextButtonText={beforeLastStepNextButtonText}
            onNextClick={saveAndNavigateNext}
            isNextClickDisabled={isBusy}
            // Using cancel button for "Save & exit"
            onCancelClick={saveAndExit}
            cancelButtonText={saveAndExitButtonText}
          />
        );
      }
      // Before campaign is created
      return (
        <StepFooter
          className={classNames('wizard-step-footer', classes.wizardFooter)}
          styleOverrides={footerStyleOverrides}
          nextButtonText={beforeLastStepNextButtonText}
          onNextClick={saveAndNavigateNext}
          isNextClickDisabled={isBusy}
          // onCancelClick={cancelAndExit}
          // Using the "skip" link-button for "Cancel"
          onSkippingLinkClick={cancelAndExit}
          skippingLinkTitle={i18n.getString(_TL_('Cancel'))}
        />
      );
    }
    if (stepIndex < stepListWithServiceInfo.length - 1) {
      // [2nd..last) steps
      return (
        <StepFooter
          className={classNames('wizard-step-footer', classes.wizardFooter)}
          styleOverrides={footerStyleOverrides}
          previousButtonAfterNext
          nextButtonText={beforeLastStepNextButtonText}
          onNextClick={saveAndNavigateNext}
          isNextClickDisabled={isBusy}
          previousButtonText={previousButtonText}
          onPreviousClick={saveAndNavigateBack}
          isPreviousClickDisabled={isBusy}
          // Using cancel button for "Save & exit"
          onCancelClick={saveAndExit}
          cancelButtonText={saveAndExitButtonText}
        />
      );
    }
    // last step
    const lastStepInfo = last(stepListWithServiceInfo);
    if (lastStepInfo.isReadOnly) {
      return (
        <StepFooter
          className={classNames('wizard-step-footer', classes.wizardFooter)}
          styleOverrides={footerStyleOverrides}
          previousButtonAfterNext
          nextButtonText={i18n.getString(_TL_('Done'))}
          onNextClick={saveAndNavigateNext}
          isNextClickDisabled={isBusy}
          previousButtonText={previousButtonText}
          onPreviousClick={saveAndNavigateBack}
          isPreviousClickDisabled={isBusy}
        />
      );
    }

    return (
      <StepFooter
        className={classNames('wizard-step-footer', classes.wizardFooter)}
        styleOverrides={footerStyleOverrides}
        previousButtonAfterNext
        nextButtonText={i18n.getString(_TL_('Save'))}
        onNextClick={saveAndNavigateNext}
        isNextClickDisabled={isBusy}
        previousButtonText={previousButtonText}
        onPreviousClick={saveAndNavigateBack}
        isPreviousClickDisabled={isBusy}
      />
    );
  }, [
    stepIndex, classes.wizardFooter, i18n, stepListWithServiceInfo,
    saveAndNavigateNext, saveAndNavigateBack, saveAndExit, cancelAndExit, isBusy,
  ]);

  return (
    <StepsContextProvider currentPath={stepInfo.id}>
      <div className={classes.wizardContainer}>
        <div className={classNames('wizard-list-container', classes.wizardListContainer)}>
          <StepProgressList getItemGlyphicon={getStepProgressItemGlyphicon} />
        </div>
        <div className={classNames('wizard-main-container', classes.wizardMainContainer)}>
          <StepContainer className={classNames('wizard-step-container', classes.wizardStepContainer)}>
            {children}
          </StepContainer>
          {stepFooter}
        </div>
      </div>
    </StepsContextProvider>
  );
});

MultiStepLayout.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  wizardState: PropTypes.shape({
    i18n: PropTypes.shape({
      getString: PropTypes.func.isRequired,
    }).isRequired,
    stepInfo: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
    stepIndex: PropTypes.number.isRequired,
    stepListWithServiceInfo: PropTypes.arrayOf(PropTypes.shape({
      stepService: PropTypes.shape({}),
    })).isRequired,
    isBusy: PropTypes.bool.isRequired,
  }).isRequired,
  wizardActions: PropTypes.shape({
    saveAndNavigateNext: PropTypes.func.isRequired,
    saveAndNavigateBack: PropTypes.func.isRequired,
    cancelAndExit: PropTypes.func.isRequired,
    saveAndExit: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    wizardContainer: PropTypes.string.isRequired,
    wizardListContainer: PropTypes.string.isRequired,
    wizardMainContainer: PropTypes.string.isRequired,
    wizardStepContainer: PropTypes.string.isRequired,
    wizardFooter: PropTypes.string.isRequired,
  }).isRequired,
};
